import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Bell,
  Menu,
  LogOut,
  Settings,
  User,
  ChevronDown,
  Moon,
  Sun,
  Command,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/auth-store';
import { getInitials } from '@/lib/utils';
import { getStoredTheme, setTheme } from '@/lib/theme';
import { LanguageSwitcher } from './language-switcher';
import { useTranslation } from 'react-i18next';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation();
  const [searchFocused, setSearchFocused] = useState(false);
  const [isDark, setIsDark] = useState(() => getStoredTheme() === 'dark');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setIsDark(!isDark);
    setTheme(next);
  };

  const initials = user ? getInitials(user.firstName, user.lastName) : 'U';

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center justify-between gap-4 border-b bg-[hsl(var(--background))]/80 backdrop-blur-xl px-6">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMenuClick}
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Search Bar */}
        <div className="hidden md:block relative">
          <div
            className={`flex items-center gap-3 rounded-xl border bg-[hsl(var(--card))] px-4 py-2 transition-all duration-300 ${
              searchFocused
                ? 'w-80 border-[hsl(var(--gold))] shadow-[0_0_0_3px_hsl(var(--gold)/0.1)]'
                : 'w-64 border-[hsl(var(--border))]'
            }`}
          >
            <Search className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
            <input
              type="text"
              placeholder={t('searchAnything')}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-[hsl(var(--muted-foreground))]"
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
            <kbd className="hidden lg:flex h-5 items-center gap-1 rounded border bg-[hsl(var(--muted))] px-1.5 text-[10px] font-medium text-[hsl(var(--muted-foreground))]">
              <Command className="h-3 w-3" />K
            </kbd>
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl hover:bg-[hsl(var(--muted))]"
          onClick={toggleTheme}
        >
          {isDark ? (
            <Sun className="h-5 w-5 text-[hsl(var(--gold))]" />
          ) : (
            <Moon className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
          )}
        </Button>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative rounded-xl hover:bg-[hsl(var(--muted))]"
            >
              <Bell className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
              {/* Notification Badge */}
              <span className="absolute right-2 top-2 flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--gold))] opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--gold))]" />
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 rounded-xl p-0">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <span className="font-display text-sm font-semibold">{t('notifications')}</span>
              <Button variant="ghost" size="sm" className="h-auto px-2 py-1 text-xs text-[hsl(var(--gold))]">
                {t('markAllRead')}
              </Button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {/* Sample notifications */}
              <div className="px-4 py-3 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer border-l-2 border-transparent hover:border-[hsl(var(--gold))]">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[hsl(var(--gold))] to-[hsl(38,70%,45%)] flex items-center justify-center text-xs font-medium text-[hsl(var(--primary))]">
                    JD
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">
                      <span className="font-medium">John Doe</span> booked a new package
                    </p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">2 minutes ago</p>
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer border-l-2 border-transparent hover:border-[hsl(var(--gold))]">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-[hsl(var(--success))] flex items-center justify-center">
                    <Bell className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">
                      Payment received for <span className="font-medium">Invoice #1234</span>
                    </p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">1 hour ago</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t px-4 py-2">
              <Button variant="ghost" className="w-full text-sm text-[hsl(var(--gold))]">
                {t('viewAllNotifications')}
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Divider */}
        <div className="hidden md:block h-6 w-px bg-[hsl(var(--border))]" />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-[hsl(var(--muted))] transition-colors outline-none">
              <Avatar className="h-8 w-8 ring-2 ring-[hsl(var(--gold))/20]">
                <AvatarImage src="" alt={user?.firstName || 'User'} />
                <AvatarFallback className="bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(222,47%,25%)] text-white text-xs font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium leading-none">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                  {user?.role || t('administrator')}
                </p>
              </div>
              <ChevronDown className="hidden md:block h-4 w-4 text-[hsl(var(--muted-foreground))]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56 rounded-xl p-1"
            sideOffset={8}
          >
            <DropdownMenuLabel className="px-3 py-2">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(222,47%,25%)] text-white text-sm font-medium">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {user?.email}
                  </p>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-3 rounded-lg cursor-pointer"
              onClick={() => navigate('/settings')}
            >
              <User className="h-4 w-4" />
              <span>{t('profile')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="gap-3 rounded-lg cursor-pointer"
              onClick={() => navigate('/settings')}
            >
              <Settings className="h-4 w-4" />
              <span>{t('settings')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-3 rounded-lg cursor-pointer text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
              <span>{t('logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
